import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildNormalizedSeries,
  dailyFromCumulativePct,
  filterRowsToWindow,
  isComparisonHistoryPending,
  type ChartZoomWindow,
  type ComparisonAccount,
  type PerformanceHistoryRange,
} from '@shared/PortfolioComparison';
import {
  BENCHMARK_COLOR,
  BENCHMARK_LABEL,
  BENCHMARK_SERIES_ID,
} from '@shared/PortfolioComparison/comparisonTypes';

const COMPARE_CHART_MARGIN = { top: 12, right: 16, left: 8, bottom: 28 };

const RANGE_OPTIONS: PerformanceHistoryRange[] = ['1D', '1W', '1M', 'ALL'];

const DAY_SECONDS = 24 * 60 * 60;
// Only 1M is sliced client-side from the inception-anchored ALL series; 1D/1W
// are fetched directly (the fetched data already IS the window), and ALL is the
// full range, so neither is client-windowed.
const RANGE_WINDOW_DAYS: Partial<Record<PerformanceHistoryRange, number>> = {
  '1M': 30,
};

/**
 * Map the selected range to a client-side x-axis window (Unix-second bounds)
 * anchored at the latest data point. Returns null when no client windowing is
 * needed (1D/1W are fetched directly; ALL is the full range) — only 1M slices
 * the inception-anchored ALL series.
 */
const windowForRange = (
  range: PerformanceHistoryRange | undefined,
  latestT: number | undefined,
): ChartZoomWindow | null => {
  if (range === undefined || latestT === undefined) return null;
  const days = RANGE_WINDOW_DAYS[range];
  if (days === undefined) return null;
  return { startT: latestT - days * DAY_SECONDS, endT: latestT };
};

const formatPctPoints = (value: number | undefined) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

const pctClass = (value: number | undefined) => {
  if (value === undefined) return '';
  return value >= 0 ? 'text-positive' : 'text-[color:var(--nq-down-soft)]';
};

interface Props {
  accounts: ComparisonAccount[];
  selectedRange?: PerformanceHistoryRange;
  onRangeChange?: (range: PerformanceHistoryRange) => void;
  loading?: boolean;
  error?: string | null;
}

const ComparisonReturnChart = ({
  accounts,
  selectedRange,
  onRangeChange,
  loading,
  error,
}: Props) => {
  const series = useMemo(() => buildNormalizedSeries(accounts), [accounts]);

  const rangeStart = useMemo(() => series[0]?.values, [series]);

  const data = useMemo(() => {
    if (series.length === 0) return [];
    return series.map((point, index, arr) => {
      const prev = index > 0 ? arr[index - 1] : undefined;
      const row: Record<string, number | string | undefined> = {
        label: point.label,
        t: point.t,
      };
      for (const acc of accounts) {
        const v = point.values[acc.accountId];
        row[acc.accountId] = v;
        if (prev) {
          const prevV = prev.values[acc.accountId];
          if (v !== undefined && prevV !== undefined) {
            row[`${acc.accountId}__day`] = dailyFromCumulativePct(v, prevV);
          }
        }
      }
      const bench = point.values[BENCHMARK_SERIES_ID];
      row[BENCHMARK_SERIES_ID] = bench;
      if (prev) {
        const prevBench = prev.values[BENCHMARK_SERIES_ID];
        if (bench !== undefined && prevBench !== undefined) {
          row[`${BENCHMARK_SERIES_ID}__day`] = dailyFromCumulativePct(bench, prevBench);
        }
      }
      return row;
    });
  }, [series, accounts]);

  // The series is inception-anchored; the range toggle only zooms the visible
  // x-axis window. Falls back to the full series if a window yields no rows.
  const visibleData = useMemo(() => {
    const latestT = data.length > 0 ? Number(data[data.length - 1]!.t) : undefined;
    const window = windowForRange(selectedRange, latestT);
    if (!window) return data;
    const windowed = filterRowsToWindow(data, window);
    return windowed.length > 0 ? windowed : data;
  }, [data, selectedRange]);

  const yDomain = useMemo<[number, number]>(() => {
    if (visibleData.length === 0) return [-1, 1];
    let min = Infinity;
    let max = -Infinity;
    for (const row of visibleData) {
      for (const acc of accounts) {
        const v = row[acc.accountId];
        if (typeof v === 'number' && Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
      const bench = row[BENCHMARK_SERIES_ID];
      if (typeof bench === 'number' && Number.isFinite(bench)) {
        if (bench < min) min = bench;
        if (bench > max) max = bench;
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [-1, 1];
    const pad = Math.max((max - min) * 0.1, 0.5);
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [visibleData, accounts]);

  const hasBenchmark = useMemo(
    () => data.some((row) => typeof row[BENCHMARK_SERIES_ID] === 'number'),
    [data],
  );

  const historyPending =
    Boolean(loading) || isComparisonHistoryPending(accounts);
  const noHistory =
    !historyPending && accounts.every((a) => !a.history);

  type TipPayloadEntry = {
    dataKey: string;
    value: number;
    color?: string;
    name?: string;
    payload?: Record<string, number | string | undefined>;
  };

  const Tip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: TipPayloadEntry[];
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;

    const benchEntry = payload.find((e) => e.dataKey === BENCHMARK_SERIES_ID);
    const benchValue =
      typeof benchEntry?.value === 'number' ? benchEntry.value : undefined;

    const portfolioEntries = payload.filter(
      (e) => e.dataKey !== BENCHMARK_SERIES_ID,
    );

    const leaderEntry = portfolioEntries.reduce<TipPayloadEntry | undefined>(
      (best, entry) => {
        if (typeof entry.value !== 'number') return best;
        if (!best || entry.value > (best.value as number)) return entry;
        return best;
      },
      undefined,
    );

    const leaderDayKey = leaderEntry
      ? `${leaderEntry.dataKey}__day`
      : undefined;
    const leaderDay =
      leaderDayKey !== undefined
        ? Number(leaderEntry?.payload?.[leaderDayKey])
        : undefined;

    const alphaVsBench =
      leaderEntry &&
      typeof leaderEntry.value === 'number' &&
      benchValue !== undefined
        ? leaderEntry.value - benchValue
        : undefined;

    return (
      <div className="pointer-events-none min-w-[220px] rounded-lg border border-solid border-[color:rgba(255,255,255,0.14)] bg-[color:var(--nq-bg-deep)]/98 px-3 py-2 text-[13px] shadow-[0_8px_24px_rgba(0,0,0,0.45)]">
        <p className="mb-1.5 text-xs text-muted-foreground">{label}</p>
        {portfolioEntries.map((entry) => {
          const acc = accounts.find((a) => a.accountId === entry.dataKey);
          if (!acc || typeof entry.value !== 'number') return null;
          const startVal = rangeStart?.[acc.accountId];
          const rangePct =
            startVal !== undefined ? entry.value - startVal : entry.value;
          return (
            <div
              key={entry.dataKey}
              className={`grid grid-cols-[minmax(0,1fr)_72px] items-center gap-2.5 whitespace-nowrap py-[3px] ${
                entry.dataKey === leaderEntry?.dataKey ? 'font-semibold' : ''
              }`}
            >
              <span className="inline-flex items-center gap-1.5 overflow-hidden text-ellipsis">
                <span
                  className="inline-block h-2 w-2 flex-none rounded-full"
                  style={{ backgroundColor: acc.color }}
                  aria-hidden
                />
                {acc.label}
              </span>
              <span
                className={`text-right font-semibold tabular-nums ${pctClass(rangePct)}`}
              >
                {formatPctPoints(rangePct)}
              </span>
            </div>
          );
        })}
        {hasBenchmark && typeof benchValue === 'number' && (
          <div className="grid grid-cols-[minmax(0,1fr)_72px] items-center gap-2.5 whitespace-nowrap py-[3px]">
            <span className="inline-flex items-center gap-1.5 overflow-hidden text-ellipsis">
              <span
                className="inline-block h-2 w-2 flex-none rounded-full"
                style={{ backgroundColor: BENCHMARK_COLOR }}
                aria-hidden
              />
              {BENCHMARK_LABEL}
            </span>
            <span className={`text-right font-semibold tabular-nums ${pctClass(benchValue)}`}>
              {formatPctPoints(benchValue)}
            </span>
          </div>
        )}
        {alphaVsBench !== undefined && (
          <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)_72px] items-center gap-2.5 whitespace-nowrap border-t border-[color:rgba(255,255,255,0.12)] pt-1.5 text-xs font-semibold">
            <span>vs SPY (leader)</span>
            <span
              className={`text-right font-semibold tabular-nums ${pctClass(alphaVsBench)}`}
            >
              {formatPctPoints(alphaVsBench)}
            </span>
          </div>
        )}
        {Number.isFinite(leaderDay) && (
          <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)_72px] items-center gap-2.5 whitespace-nowrap border-t border-[color:rgba(255,255,255,0.12)] pt-1.5 text-xs font-semibold">
            <span>Daily delta</span>
            <span className={`text-right font-semibold tabular-nums ${pctClass(leaderDay)}`}>
              {formatPctPoints(leaderDay)}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-solid border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.025)] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
      <div className="mb-3 flex flex-row flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="m-0 text-[1.05rem] font-semibold text-foreground">Return overlay</h3>
          <p className="mt-1 text-[0.85rem] text-muted-foreground">
            Each line is cumulative % return. 1D/1W show intraday movement
            rebased to the window start; 1M/ALL are since inception.
          </p>
        </div>
        {onRangeChange && (
          <div
            className="inline-flex gap-1 rounded-lg border border-solid border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.04)] p-[3px]"
            role="group"
            aria-label="Time range"
          >
            {RANGE_OPTIONS.map((range) => (
              <button
                key={range}
                type="button"
                className={`cursor-pointer appearance-none rounded-md border-none px-2.5 py-1 text-[0.78rem] font-semibold tracking-[0.04em] ${
                  selectedRange === range
                    ? 'bg-[color:rgba(255,255,255,0.1)] text-foreground'
                    : 'bg-transparent text-muted-foreground'
                }`}
                onClick={() => onRangeChange(range)}
                aria-pressed={selectedRange === range}
              >
                {range}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-2 rounded-lg border border-solid border-negative/25 bg-negative/8 px-2.5 py-2 text-[0.85rem] text-[color:var(--nq-down-soft)]">
          {error}
        </div>
      )}

      {historyPending ? (
        <div
          className="relative mt-2 h-[280px] overflow-hidden rounded-lg border border-solid border-[color:rgba(255,255,255,0.05)] bg-[color:rgba(255,255,255,0.02)]"
          aria-busy="true"
        >
          <div className="absolute inset-[16px_12px_28px_48px] bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:100%_36px,72px_100%] opacity-60" />
          <div className="absolute left-12 right-3 top-[38%] h-[3px] -rotate-[4deg] animate-pulse rounded-full bg-muted opacity-90" />
          <div className="absolute left-12 top-[58%] h-[3px] w-[78%] rotate-[3deg] animate-pulse rounded-full bg-muted opacity-65" />
        </div>
      ) : noHistory ? (
        <div className="grid h-[200px] place-items-center text-[0.9rem] text-muted-foreground">
          {loading
            ? 'Loading performance history…'
            : 'Performance history is not available for this view.'}
        </div>
      ) : data.length === 0 ? (
        <div className="grid h-[200px] place-items-center text-[0.9rem] text-muted-foreground">
          {loading
            ? 'Loading performance history…'
            : 'No overlapping data points across selected portfolios.'}
        </div>
      ) : (
        <div className="flex flex-col">
          {/* Counter the global `.recharts-wrapper { min-height: 350px }` in
              index.css so the compact 280px compare chart keeps its height
              (was the `.nq-cmp__chart-plot .recharts-wrapper` override). */}
          <div className="w-full overflow-hidden [&_.recharts-cartesian-grid]:min-h-0! [&_.recharts-wrapper]:min-h-0!">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={visibleData}
                margin={COMPARE_CHART_MARGIN}
              >
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="4 4" />
              <XAxis
                dataKey="label"
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={28}
              />
              <YAxis
                domain={yDomain}
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
                width={56}
              />
              <Tooltip
                content={<Tip />}
                wrapperStyle={{ zIndex: 20, outline: 'none' }}
                contentStyle={{
                  background: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                  padding: 0,
                  margin: 0,
                }}
              />
              {accounts.map((acc) => (
                <Line
                  key={acc.accountId}
                  type="monotone"
                  dataKey={acc.accountId}
                  name={acc.label}
                  stroke={acc.color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              ))}
              {hasBenchmark && (
                <Line
                  type="monotone"
                  dataKey={BENCHMARK_SERIES_ID}
                  name={BENCHMARK_LABEL}
                  stroke={BENCHMARK_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              )}
            </LineChart>
          </ResponsiveContainer>
          </div>
          <div
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5 border-t border-[color:rgba(255,255,255,0.06)] px-3 py-4"
            aria-label="Chart series"
          >
            {accounts.map((acc) => (
              <span
                key={acc.accountId}
                className="inline-flex items-center gap-1.5 text-[0.8rem] leading-none text-foreground/70"
              >
                <span
                  className="inline-block h-[3px] w-3.5 flex-none rounded-[2px]"
                  style={{ backgroundColor: acc.color }}
                  aria-hidden
                />
                <span className="whitespace-nowrap">{acc.label}</span>
              </span>
            ))}
            {hasBenchmark && (
              <span className="inline-flex items-center gap-1.5 text-[0.8rem] leading-none text-foreground/70">
                <span
                  className="inline-block h-0 w-4 flex-none border-t-2 border-dashed bg-transparent"
                  style={{ borderColor: BENCHMARK_COLOR }}
                  aria-hidden
                />
                <span className="whitespace-nowrap">{BENCHMARK_LABEL}</span>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonReturnChart;
